<template>
	<div>
		<JuniperPageHeader back="/juniper/accessibility-stats" :title="client.name || 'Accessibility Stats'" :subtitle="subtitle" />

		<!-- Latest report vs the one it is compared with -->
		<div class="axs-section axs-summary-section mb-3">
			<div class="axs-summary">
				<div class="axs-summary-item">
					<div class="axs-summary-label">Score</div>
					<div class="axs-summary-value">
						{{ a && a.score !== null ? a.score + '%' : '—' }}
						<span v-if="a && b" class="delta" :class="deltaClass(a.score - b.score, true)">{{ signed(a.score - b.score) }}</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Errors</div>
					<div class="axs-summary-value">
						{{ a ? a.errors : '—' }}
						<span v-if="a && b" class="delta" :class="deltaClass(a.errors - b.errors)">{{ signed(a.errors - b.errors) }}</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Warnings</div>
					<div class="axs-summary-value">
						{{ a ? a.warnings : '—' }}
						<span v-if="a && b" class="delta" :class="deltaClass(a.warnings - b.warnings)">{{ signed(a.warnings - b.warnings) }}</span>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Page status</div>
					<div class="axs-summary-value axs-summary-value--sm">
						<template v-if="a">
							<span class="nowrap me-3"><span class="status-dot good"></span>{{ a.good_pages }}</span>
							<span class="nowrap me-3"><span class="status-dot warn"></span>{{ a.warn_pages }}</span>
							<span class="nowrap"><span class="status-dot poor"></span>{{ a.poor_pages }}</span>
							<span class="cell-muted ms-2">of {{ a.pages }}</span>
						</template>
						<template v-else>—</template>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Errors by type</div>
					<div class="axs-summary-value axs-summary-value--sm">
						<template v-if="a && a.errors_by_source">
							<span class="source-badge content" title="Content errors">C {{ a.errors_by_source.content }}</span>
							<span class="source-badge theme" title="Theme errors">T {{ a.errors_by_source.theme }}</span>
							<span class="source-badge document" title="Document errors">D {{ a.errors_by_source.document }}</span>
							<span v-if="a.errors_by_source.unknown" class="source-badge unknown" title="Unclassified (scanned before source tracking)">? {{ a.errors_by_source.unknown }}</span>
						</template>
						<template v-else>—</template>
					</div>
				</div>
				<div class="axs-summary-item">
					<div class="axs-summary-label">Reports</div>
					<div class="axs-summary-value">{{ reports.length }}</div>
				</div>
			</div>
		</div>

		<!-- Trend over finished reports -->
		<div class="axs-section mb-3 trend-section">
			<div class="trend-grid">
				<AdaTrendChart title="Score" :points="trendPoints" :series="scoreSeries" :y-max="100" suffix="%" />
				<AdaTrendChart title="Errors & warnings" :points="trendPoints" :series="issueSeries" />
			</div>
			<p class="trend-note">Last {{ trendPoints.length }} finished reports, oldest → newest. Hover for values.</p>
		</div>

		<div class="axs-section mb-4">
			<div class="filter-tabs-container">
				<div class="filter-tabs-group">
					<div class="filter-tabs">
						<button class="filter-tab" :class="{ active: tab === 'changes' }" @click="tab = 'changes'">
							<span class="filter-tab-text">Changes</span>
						</button>
						<button class="filter-tab" :class="{ active: tab === 'reports' }" @click="tab = 'reports'">
							<span class="filter-tab-text">Reports</span>
							<span class="filter-tab-count">{{ reports.length }}</span>
						</button>
					</div>
				</div>

				<div class="toolbar-right">
					<template v-if="tab === 'changes' && finishedReports.length">
						<label class="compare-label" for="axs-compare-a">Compare</label>
						<select id="axs-compare-a" class="compare-select" :value="a ? a.id : ''" @change="setCompare('a', $event.target.value)">
							<option v-for="r in finishedReports" :key="r.id" :value="r.id">{{ reportLabel(r) }}</option>
						</select>
						<label class="compare-label" for="axs-compare-b">with</label>
						<select id="axs-compare-b" class="compare-select" :value="b ? b.id : ''" @change="setCompare('b', $event.target.value)">
							<option v-for="r in olderThanA" :key="r.id" :value="r.id">{{ reportLabel(r) }}</option>
						</select>
					</template>
					<button class="toolbar-icon-btn" title="Reload" :disabled="loading" @click="load">
						<font-awesome-icon :icon="['fas', 'sync']" :spin="loading" />
					</button>
				</div>
			</div>

			<div class="table-body-wrap" :class="{ 'is-loading': loading }">
				<div :class="{ 'is-blurred': loading }" class="table-body">
					<!-- Changes -->
					<template v-if="tab === 'changes'">
						<div v-if="!compare" class="empty-state">
							<div class="empty-state-icon"><font-awesome-icon :icon="['fas', 'universal-access']" /></div>
							<h3 class="empty-state-title">{{ error ? 'Could not load client stats' : 'No finished report yet' }}</h3>
							<p class="empty-state-description">{{ error || 'Changes appear once the client has a finished audit.' }}</p>
						</div>

						<template v-else>
							<div class="sub-head">
								<span class="sub-title">Issues by rule</span>
								<div class="filter-tabs filter-tabs--sm">
									<button v-for="f in ruleFilters" :key="f.key" class="filter-tab" :class="{ active: ruleFilter === f.key }" @click="ruleFilter = f.key">
										<span class="filter-tab-text">{{ f.label }}</span>
										<span class="filter-tab-count">{{ ruleCount(f.key) }}</span>
									</button>
								</div>
							</div>
							<div class="axs-table grid-rules">
								<div class="table-header">
									<div class="header-cell">Issue</div>
									<div class="header-cell">Severity</div>
									<div class="header-cell">Engines</div>
									<div class="header-cell text-end">{{ b ? 'Before' : '' }}</div>
									<div class="header-cell text-end">Now</div>
									<div class="header-cell text-end">Change</div>
								</div>
								<div v-if="pagedRules.length === 0" class="empty-state empty-state--sm">
									<p class="empty-state-description">Nothing here.</p>
								</div>
								<div v-for="r in pagedRules" :key="r.severity + r.name" class="table-row">
									<div class="table-cell client-name-text" :title="r.name">{{ r.name }}</div>
									<div class="table-cell"><span class="level-badge" :class="r.severity">{{ r.severity }}</span></div>
									<div class="table-cell cell-muted">{{ r.engines.join(' · ') }}</div>
									<div class="table-cell text-end cell-muted">{{ r.b === null ? '' : r.b }}</div>
									<div class="table-cell text-end">{{ r.a }}</div>
									<div class="table-cell text-end">
										<span v-if="b" class="delta" :class="deltaClass(r.delta)">{{ r.delta === 0 ? '—' : signed(r.delta) }}</span>
									</div>
								</div>
								<StatsPagination :total="filteredRules.length" :page.sync="rulePage" :per-page.sync="rulePerPage" />
							</div>

							<template v-if="b">
								<div class="sub-head">
									<span class="sub-title">Pages <span class="cell-muted">· errors per page, {{ compare.unchanged }} unchanged</span></span>
									<div class="filter-tabs filter-tabs--sm">
										<button v-for="f in pageFilters" :key="f.key" class="filter-tab" :class="{ active: pageFilter === f.key }" @click="pageFilter = f.key">
											<span class="filter-tab-text">{{ f.label }}</span>
											<span class="filter-tab-count">{{ compare.pages[f.key].length }}</span>
										</button>
									</div>
								</div>
								<div class="axs-table grid-pages">
									<div class="table-header">
										<div class="header-cell">Page</div>
										<div class="header-cell text-end">Before</div>
										<div class="header-cell text-end">Now</div>
										<div class="header-cell text-end">Change</div>
									</div>
									<div v-if="pagedPages.length === 0" class="empty-state empty-state--sm">
										<p class="empty-state-description">No pages in this group.</p>
									</div>
									<div v-for="p in pagedPages" :key="p.url" class="table-row">
										<div class="table-cell" :title="p.url">
											<a :href="p.url" target="_blank" rel="noopener noreferrer" class="page-link">{{ shortUrl(p.url) }}</a>
										</div>
										<div class="table-cell text-end cell-muted">{{ p.b === null ? '—' : p.b }}</div>
										<div class="table-cell text-end">{{ p.a === null ? '—' : p.a }}</div>
										<div class="table-cell text-end">
											<span class="delta" :class="deltaClass(p.delta)">{{ signed(p.delta) }}</span>
										</div>
									</div>
									<StatsPagination :total="(compare.pages[pageFilter] || []).length" :page.sync="pagePage" :per-page.sync="pagePerPage" />
								</div>
							</template>
							<p v-else class="trend-note px-4">This is the client's first finished report — nothing to compare with yet.</p>
						</template>
					</template>

					<!-- Reports -->
					<div v-else class="axs-table grid-reports">
						<div class="table-header">
							<div class="header-cell">Date</div>
							<div class="header-cell">Run</div>
							<div class="header-cell">Status</div>
							<div class="header-cell text-end">Pages</div>
							<div class="header-cell">Page status</div>
							<div class="header-cell text-end">Errors</div>
							<div class="header-cell text-end">Warnings</div>
							<div class="header-cell text-end">Score</div>
							<div class="header-cell text-end">Change</div>
						</div>
						<div v-if="reports.length === 0" class="empty-state empty-state--sm">
							<p class="empty-state-description">No reports yet.</p>
						</div>
						<div v-for="r in pagedReports" :key="r.id" class="table-row is-clickable" title="Open report" @click="reportModal = r.id">
							<div class="table-cell">
								<span :title="formatUtc(r.finished_at || r.started_at)">{{ formatUtc(r.finished_at || r.started_at, 'MMM D, YYYY h:mm A') }}</span>
							</div>
							<div class="table-cell">
								<span class="interval-badge">{{ r.run_type }}</span>
								<span v-if="r.run_type === 'manual' && r.triggered_by" class="cell-muted ms-1">{{ r.triggered_by }}</span>
							</div>
							<div class="table-cell"><span class="level-badge" :class="r.status === 'finished' ? 'done' : 'info'">{{ r.status }}</span></div>
							<div class="table-cell text-end">
								{{ r.pages }}<span v-if="r.pages_reused" class="cell-muted" :title="r.pages_reused + ' unchanged pages reused from the previous scan'"> ({{ r.pages_reused }} reused)</span>
							</div>
							<div class="table-cell">
								<div v-if="r.pages" class="status-cell">
									<div class="status-bar">
										<span class="seg good" :style="{ width: pct(r.good_pages, r.pages) }"></span>
										<span class="seg warn" :style="{ width: pct(r.warn_pages, r.pages) }"></span>
										<span class="seg poor" :style="{ width: pct(r.poor_pages, r.pages) }"></span>
									</div>
									<span class="status-nums">
										<span class="good-text">{{ r.good_pages }}</span> · <span class="warn-text">{{ r.warn_pages }}</span> ·
										<span class="poor-text">{{ r.poor_pages }}</span>
									</span>
								</div>
								<span v-else class="cell-muted">—</span>
							</div>
							<div class="table-cell text-end">{{ r.errors }}</div>
							<div class="table-cell text-end">{{ r.warnings }}</div>
							<div class="table-cell text-end">
								<span v-if="r.score !== null" class="score-badge" :class="scoreClass(r.score)">{{ r.score }}%</span>
								<span v-else class="cell-muted">—</span>
							</div>
							<div class="table-cell text-end">
								<span v-if="previousOf(r)" class="delta" :class="deltaClass(r.errors - previousOf(r).errors)" title="Errors vs the previous finished report">
									{{ r.errors - previousOf(r).errors === 0 ? '—' : signed(r.errors - previousOf(r).errors) }}
								</span>
							</div>
						</div>
						<StatsPagination :total="reports.length" :page.sync="reportPage" :per-page.sync="reportPerPage" />
					</div>
				</div>

				<div v-if="loading" class="table-loading">
					<div class="table-loading__box">
						<span class="spinner-border spinner-border-sm me-2" role="status"></span>
						Loading client stats…
					</div>
				</div>
			</div>
		</div>

		<ReportDetailsModal v-if="reportModal" :report-id="reportModal" :client-id="clientId" @close="reportModal = null" />
	</div>
</template>

<script>
import api from '@/api'
import { formatUtc } from '@/utils/time'
import AdaTrendChart from '@/components/juniper/ada-auditor/AdaTrendChart.vue'
import StatsPagination from '@/components/juniper/ada-auditor/StatsPagination.vue'
import ReportDetailsModal from '@/components/juniper/ada-auditor/ReportDetailsModal.vue'
import { scheduleWhen, nextScan } from '@/components/juniper/ada-auditor/auditSchedule'

// Validated categorical slots (dataviz palette): 1 blue, 2 orange
const SERIES_1 = '#2a78d6'
const SERIES_2 = '#eb6834'
const TREND_REPORTS = 30

export default {
	name: 'AccessibilityClientStats',
	components: { AdaTrendChart, StatsPagination, ReportDetailsModal },
	data() {
		return {
			loading: false,
			error: null,
			client: {},
			reports: [],
			compare: null,
			tab: 'changes',
			ruleFilter: 'error',
			ruleFilters: [
				{ key: 'error', label: 'Errors' },
				{ key: 'warning', label: 'Warnings' },
				{ key: 'changed', label: 'Changed' },
			],
			pageFilter: 'worse',
			pageFilters: [
				{ key: 'worse', label: 'Worse' },
				{ key: 'better', label: 'Better' },
				{ key: 'added', label: 'New' },
				{ key: 'removed', label: 'Removed' },
			],
			rulePage: 1,
			rulePerPage: 10,
			pagePage: 1,
			pagePerPage: 10,
			reportPage: 1,
			reportPerPage: 10,
			reportModal: null,
			scoreSeries: [{ key: 'score', label: 'Score', color: SERIES_1 }],
			issueSeries: [
				{ key: 'errors', label: 'Errors', color: SERIES_1 },
				{ key: 'warnings', label: 'Warnings', color: SERIES_2 },
			],
		}
	},
	computed: {
		clientId() {
			return Number(this.$route.params.clientId)
		},
		a() {
			return this.compare ? this.compare.a : null
		},
		b() {
			return this.compare ? this.compare.b : null
		},
		subtitle() {
			const parts = [(this.client.url || '').replace(/^https?:\/\//, '').replace(/\/$/, '')]
			const schedule = this.client.schedule
			if (schedule) {
				parts.push(`${schedule.interval} at ${scheduleWhen(schedule)}`)
				parts.push('next: ' + nextScan(schedule, this.reports[0]).text.toLowerCase())
			} else {
				parts.push('auto scan off')
			}
			if (this.client.engine) parts.push(this.client.engine.split('+').join(' · '))
			return parts.filter(Boolean).join('  ·  ')
		},
		finishedReports() {
			return this.reports.filter((r) => r.status === 'finished')
		},
		olderThanA() {
			return this.a ? this.finishedReports.filter((r) => r.id < this.a.id) : []
		},
		trendPoints() {
			return this.finishedReports
				.slice(0, TREND_REPORTS)
				.reverse()
				.map((r) => ({
					label: formatUtc(r.finished_at || r.started_at, 'MMM D'),
					tooltip: formatUtc(r.finished_at || r.started_at, 'MMM D, YYYY h:mm A'),
					values: { score: r.score || 0, errors: r.errors, warnings: r.warnings },
				}))
		},
		filteredRules() {
			const rules = this.compare ? this.compare.rules : []
			if (this.ruleFilter === 'changed') return rules.filter((r) => r.delta !== 0).sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta))
			return rules.filter((r) => r.severity === this.ruleFilter && (r.a > 0 || r.delta !== 0))
		},
		pagedRules() {
			return this.slice(this.filteredRules, this.rulePage, this.rulePerPage)
		},
		pagedPages() {
			return this.slice(this.compare ? this.compare.pages[this.pageFilter] || [] : [], this.pagePage, this.pagePerPage)
		},
		pagedReports() {
			return this.slice(this.reports, this.reportPage, this.reportPerPage)
		},
	},
	watch: {
		ruleFilter() {
			this.rulePage = 1
		},
		pageFilter() {
			this.pagePage = 1
		},
		'$route.params.clientId'() {
			this.load()
		},
	},
	created() {
		this.load()
	},
	methods: {
		formatUtc,
		async load(params = {}) {
			this.loading = true
			this.error = null
			try {
				const { data } = await api.get(`/juniper/ada-auditor/stats/clients/${this.clientId}`, { params })
				this.client = data.client || {}
				this.reports = data.reports || []
				this.compare = data.compare || null
				this.rulePage = 1
				this.pagePage = 1
			} catch (e) {
				this.compare = null
				this.error = (e.response && e.response.data && e.response.data.message) || 'Request failed'
			} finally {
				this.loading = false
			}
		},
		setCompare(which, id) {
			const params = { a: this.a ? this.a.id : undefined, b: this.b ? this.b.id : undefined, [which]: Number(id) }
			// picking a newer/older "a" drops a "b" that is no longer older than it
			if (which === 'a' && params.b && params.b >= params.a) delete params.b
			this.load(params)
		},
		ruleCount(key) {
			const rules = this.compare ? this.compare.rules : []
			if (key === 'changed') return rules.filter((r) => r.delta !== 0).length
			return rules.filter((r) => r.severity === key && (r.a > 0 || r.delta !== 0)).length
		},
		slice(list, page, perPage) {
			const from = (page - 1) * perPage
			return list.slice(from, from + perPage)
		},
		previousOf(report) {
			if (report.status !== 'finished') return null
			return this.finishedReports.find((r) => r.id < report.id) || null
		},
		reportLabel(r) {
			return `${formatUtc(r.finished_at || r.started_at, 'MMM D, YYYY')} · ${r.score === null ? '—' : r.score}% · ${r.errors} errors`
		},
		// fewer errors/warnings = better; for score higher = better
		deltaClass(delta, higherIsBetter = false) {
			if (!delta) return 'flat'
			return (delta > 0) === higherIsBetter ? 'better' : 'worse'
		},
		signed(n) {
			return n > 0 ? `+${n}` : String(n)
		},
		shortUrl(url) {
			return (url || '').replace(/^https?:\/\//, '')
		},
		pct(part, total) {
			return total ? (100 * part) / total + '%' : '0%'
		},
		scoreClass(score) {
			if (score >= 90) return 'good'
			if (score >= 70) return 'warn'
			return 'poor'
		},
	},
}
</script>

<style scoped src="./accessibility-stats.css"></style>
<style scoped>
.trend-section {
	padding: 16px 24px 8px;
}

.trend-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
	gap: 32px;
}

.trend-note {
	font-size: 12px;
	color: #6b7280;
	margin: 8px 0 8px;
}

.compare-label {
	font-size: 13px;
	color: #6b7280;
	margin: 0;
}

.compare-select {
	border: 1px solid #d1d5db;
	border-radius: 6px;
	padding: 6px 8px;
	font-size: 13px;
	background: white;
	max-width: 280px;
}

.sub-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	padding: 14px 24px 10px;
	border-top: 1px solid #f3f4f6;
}

.sub-title {
	font-family: var(--axs-mono);
	font-size: 12px;
	font-weight: 600;
	color: #111827;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.filter-tabs--sm .filter-tab {
	padding: 4px 12px;
	font-size: 13px;
}

.delta {
	font-family: var(--axs-mono);
	font-size: 12px;
	font-weight: 600;
	margin-left: 6px;
}

.delta.better {
	color: #15803d;
}

.delta.worse {
	color: #b91c1c;
}

.delta.flat {
	color: #9ca3af;
}

.level-badge.error {
	background-color: #fee2e2;
	color: #991b1b;
}

.level-badge.warning {
	background-color: #fef3c7;
	color: #92400e;
}

.level-badge.done {
	background-color: #dcfce7;
	color: #166534;
}

.page-link {
	color: #1f2937;
	text-decoration: none;
}

.page-link:hover {
	text-decoration: underline;
}

.empty-state--sm {
	padding: 20px 24px;
}

.grid-rules .table-header,
.grid-rules .table-row {
	grid-template-columns: minmax(240px, 2fr) 90px 120px 70px 70px 70px;
	min-width: 760px;
}

.grid-pages .table-header,
.grid-pages .table-row {
	grid-template-columns: minmax(300px, 1fr) 80px 80px 80px;
	min-width: 600px;
}

.grid-reports .table-header,
.grid-reports .table-row {
	grid-template-columns: 170px minmax(140px, 1fr) 90px 150px 150px 70px 80px 70px 70px;
	min-width: 1050px;
}
</style>
